// Order stages (SPEC section 5) on top of the legacy status vocabulary the
// Worker keeps (DECISIONS): the app shows New, Accepted, Packed, Out for
// delivery / Ready for pickup, Delivered, and the two refund exits (the shop
// refunds the buyer itself: refunds.ts).

import { csvText } from './csv.ts';
import { openWindowEnd, type ShopHours } from './hours.ts';

export type LegacyStatus =
  | 'Pending' | 'Accepted' | 'Processing' | 'Ready for Pickup' | 'Assigned' | 'Delivering' | 'At Door'
  | 'Delivered' | 'Picked Up' | 'Completed' | 'Rejected' | 'Cancelled' | 'Returned' | 'Failed';

export type OrderStage = 'new' | 'accepted' | 'packed' | 'out' | 'ready' | 'delivered' | 'rejected' | 'cancelled';

const STAGES: Record<string, OrderStage> = {
  Pending: 'new', Accepted: 'accepted', Processing: 'packed', Assigned: 'packed', 'Ready for Pickup': 'ready',
  Delivering: 'out', 'At Door': 'out', Delivered: 'delivered', 'Picked Up': 'delivered', Completed: 'delivered',
  Rejected: 'rejected', Cancelled: 'cancelled', Returned: 'cancelled', Failed: 'cancelled',
};

export const STAGE_LABELS: Record<OrderStage, string> = {
  new: 'New', accepted: 'Accepted', packed: 'Packed', out: 'Out for delivery', ready: 'Ready for pickup',
  delivered: 'Delivered', rejected: 'Rejected', cancelled: 'Cancelled',
};

// A short stay runs through the same stages, worded for a place.
export const STAY_STAGE_LABELS: Partial<Record<OrderStage, string>> = { packed: 'Getting ready', ready: 'Ready for check-in', delivered: 'Checked in' };

export function orderStage(status: unknown): OrderStage {
  return STAGES[String(status)] ?? 'new';
}

export function isFinished(status: unknown): boolean {
  return ['delivered', 'rejected', 'cancelled'].includes(orderStage(status));
}

// Steps shown on the order page for each way the order reaches the buyer.
export function orderSteps(fulfilment: 'delivery' | 'pickup'): OrderStage[] {
  return fulfilment === 'pickup' ? ['new', 'accepted', 'packed', 'ready', 'delivered'] : ['new', 'accepted', 'packed', 'out', 'delivered'];
}

export const DELIVERY_METHODS = ['self', 'own_rider', 'pickup', 'guildite_rider'] as const;
export type DeliveryMethod = (typeof DELIVERY_METHODS)[number];
export const DELIVERY_METHOD_LABELS: Record<DeliveryMethod, string> = {
  self: 'Deliver myself',
  own_rider: 'Assign my own rider',
  pickup: 'Buyer picks up',
  guildite_rider: 'Guildite rider',
};

export const ACCEPT_WINDOW_MS = 2 * 60 * 60 * 1000;
export const CANCEL_WINDOW_MS = 24 * 60 * 60 * 1000;
export const CODE_MAX_TRIES = 5;
export const CODE_LOCK_MS = 15 * 60 * 1000;

export type CancelCheck = { ok: true } | { ok: false; reason: 'not_paid' | 'too_late' | 'started' | 'finished' };

// Buyer cancel: within 24 hours of paying and only while New or Accepted.
export function buyerCancelCheck(order: { status?: unknown; paymentStatus?: unknown; paidAt?: unknown }, now: number): CancelCheck {
  const stage = orderStage(order.status);
  if (isFinished(order.status)) return { ok: false, reason: 'finished' };
  if (order.paymentStatus !== 'paid') return { ok: false, reason: 'not_paid' };
  if (stage !== 'new' && stage !== 'accepted') return { ok: false, reason: 'started' };
  const paidAt = Date.parse(String(order.paidAt || ''));
  if (!Number.isFinite(paidAt) || now - paidAt >= CANCEL_WINDOW_MS) return { ok: false, reason: 'too_late' };
  return { ok: true };
}

export const CANCEL_REFUSALS: Record<Exclude<CancelCheck, { ok: true }>['reason'], string> = {
  not_paid: 'This order was not paid, so there is nothing to refund.',
  too_late: 'The 24-hour cancel window has passed. Message the shop, or report a problem.',
  started: 'The shop has already packed this order. Message the shop, or report a problem.',
  finished: 'This order is already finished.',
};

// Two hours of the shop being open, from payment. Orders keep the shop's
// hours from checkout (`acceptHours`), so a night order waits for morning.
export function acceptDeadline(order: { paidAt?: unknown; createdAt?: unknown; acceptHours?: unknown }): number | null {
  const start = Date.parse(String(order.paidAt || order.createdAt || ''));
  if (!Number.isFinite(start)) return null;
  const hours = order.acceptHours && typeof order.acceptHours === 'object' ? order.acceptHours as ShopHours : null;
  return hours ? openWindowEnd(hours, new Date(start), ACCEPT_WINDOW_MS / 60_000).getTime() : start + ACCEPT_WINDOW_MS;
}

// A shop's order history for a spreadsheet (Orders → Export order history).
export type OrderExportRow = { id: string; date: string; status: string; fulfilment: 'delivery' | 'pickup'; items: string; totalMinor: number; receiveMinor: number };

export function ordersCsv(rows: OrderExportRow[]): string {
  return csvText([
    ['Order', 'Date', 'Status', 'Delivery', 'Items', 'Buyer paid (GHS)', 'You receive (GHS)'],
    ...rows.map(row => [row.id, row.date.slice(0, 10), STAGE_LABELS[orderStage(row.status)], row.fulfilment === 'pickup' ? 'Pickup' : 'Delivery', row.items, (row.totalMinor / 100).toFixed(2), (row.receiveMinor / 100).toFixed(2)]),
  ]);
}
